import * as React from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronsUpDown, Search } from 'lucide-react';
import { cn } from '../../utils';
import type { SelectOption } from './select';

export interface MultiSelectProps {
  options: SelectOption[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  selectedText?: (count: number) => string;
  disabled?: boolean;
  className?: string;
  triggerClassName?: string;
  id?: string;
  'aria-label'?: string;
  size?: 'default' | 'sm';
  searchable?: boolean;
  searchPlaceholder?: string;
  noResultsText?: string;
  disablePortal?: boolean;
}

const MultiSelect = React.forwardRef<HTMLButtonElement, MultiSelectProps>(
  (
    {
      options,
      value,
      onChange,
      placeholder = 'Select...',
      selectedText = (count) => `${count} selected`,
      disabled = false,
      className,
      triggerClassName,
      id,
      'aria-label': ariaLabel,
      size = 'default',
      searchable = false,
      searchPlaceholder = 'Search...',
      noResultsText = 'No results found.',
      disablePortal = false,
    },
    ref,
  ) => {
    const [open, setOpen] = React.useState(false);
    const [search, setSearch] = React.useState('');
    const containerRef = React.useRef<HTMLDivElement>(null);
    const dropdownRef = React.useRef<HTMLDivElement>(null);
    const searchRef = React.useRef<HTMLInputElement>(null);
    const listboxId = React.useId();
    const [dropdownStyle, setDropdownStyle] =
      React.useState<React.CSSProperties>({
        position: 'fixed',
        top: 0,
        left: 0,
        visibility: 'hidden',
      });

    const selected = React.useMemo(() => new Set(value), [value]);
    const filtered = React.useMemo(() => {
      const query = search.trim().toLowerCase();
      if (!query) return options;
      return options.filter(
        (option) =>
          option.label.toLowerCase().includes(query) ||
          option.value.toLowerCase().includes(query),
      );
    }, [options, search]);

    React.useEffect(() => {
      if (!open) return;
      const onPointerDown = (event: MouseEvent) => {
        const target = event.target as Node;
        if (
          !containerRef.current?.contains(target) &&
          !dropdownRef.current?.contains(target)
        ) {
          setOpen(false);
        }
      };
      document.addEventListener('mousedown', onPointerDown);
      return () => document.removeEventListener('mousedown', onPointerDown);
    }, [open]);

    React.useLayoutEffect(() => {
      if (!open || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const gap = 8;
      const estimatedHeight = 300;
      const placeAbove =
        window.innerHeight - rect.bottom < estimatedHeight &&
        rect.top > window.innerHeight - rect.bottom;

      if (disablePortal) {
        setDropdownStyle({
          position: 'absolute',
          ...(placeAbove
            ? { bottom: '100%', marginBottom: gap }
            : { top: '100%', marginTop: gap }),
          left: 0,
          width: '100%',
          zIndex: 9999,
        });
      } else {
        setDropdownStyle({
          position: 'fixed',
          ...(placeAbove
            ? { bottom: window.innerHeight - rect.top + gap }
            : { top: rect.bottom + gap }),
          left: rect.left,
          width: Math.max(rect.width, 240),
          zIndex: 9999,
          pointerEvents: 'auto',
        });
      }
    }, [disablePortal, open]);

    React.useEffect(() => {
      if (open && searchable) {
        requestAnimationFrame(() => searchRef.current?.focus());
      }
      if (!open) setSearch('');
    }, [open, searchable]);

    function toggle(option: SelectOption) {
      if (option.disabled) return;
      onChange(
        selected.has(option.value)
          ? value.filter((item) => item !== option.value)
          : [...value, option.value],
      );
    }

    const selectedOptions = options.filter((option) =>
      selected.has(option.value),
    );
    const displayValue =
      selectedOptions.length === 0
        ? placeholder
        : selectedOptions.length <= 2
          ? selectedOptions.map((option) => option.label).join(', ')
          : selectedText(selectedOptions.length);

    const dropdown = open ? (
      <div
        ref={dropdownRef}
        data-ui-popover
        style={dropdownStyle}
        className="animate-fade-in rounded-xl border-2 border-border bg-card shadow-hard"
      >
        {searchable && (
          <div className="border-b-2 border-border p-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Escape') setOpen(false);
                }}
                placeholder={searchPlaceholder}
                className="h-9 w-full rounded-lg bg-muted pl-8 pr-3 text-sm outline-none"
              />
            </div>
          </div>
        )}
        <ul
          id={listboxId}
          role="listbox"
          aria-multiselectable="true"
          className="max-h-60 overflow-y-auto p-1"
        >
          {filtered.length === 0 && (
            <li className="px-3 py-2 text-sm text-muted-foreground">
              {noResultsText}
            </li>
          )}
          {filtered.map((option) => {
            const checked = selected.has(option.value);
            return (
              <li key={option.value} role="presentation">
                <button
                  type="button"
                  role="option"
                  aria-selected={checked}
                  disabled={option.disabled}
                  onClick={() => toggle(option)}
                  className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <span
                    aria-hidden="true"
                    className={cn(
                      'flex size-5 shrink-0 items-center justify-center rounded-md border-2 border-border bg-background',
                      checked && 'border-riso-pink bg-riso-pink text-white',
                    )}
                  >
                    {checked && <Check className="size-3.5" />}
                  </span>
                  <span className="truncate">{option.label}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    ) : null;

    return (
      <div ref={containerRef} className={cn('relative', className)}>
        <button
          ref={ref}
          id={id}
          type="button"
          role="combobox"
          aria-label={ariaLabel}
          aria-expanded={open}
          aria-controls={open ? listboxId : undefined}
          aria-haspopup="listbox"
          disabled={disabled}
          onClick={() => setOpen((current) => !current)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') setOpen(false);
          }}
          className={cn(
            'flex w-full items-center justify-between rounded-xl border-2 border-border bg-muted font-body transition-all duration-200 ease-spring enabled:hover:border-foreground/40 focus-visible:border-foreground focus-visible:shadow-hard focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
            size === 'default' && 'h-12 px-4 text-base',
            size === 'sm' && 'h-9 px-3 text-sm',
            selectedOptions.length === 0 && 'text-muted-foreground',
            triggerClassName,
          )}
        >
          <span className="truncate">{displayValue}</span>
          <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
        </button>
        {dropdown &&
          (disablePortal ? dropdown : createPortal(dropdown, document.body))}
      </div>
    );
  },
);
MultiSelect.displayName = 'MultiSelect';

export { MultiSelect };
